import type { Asset } from '../types'
import { API_BASE, authHeaders } from './apiAuth'
import { needsSigning } from './signedUrls'

/**
 * Thumbnail links from the Worker, which serves them from Cloudflare R2
 * -------------------------------------------------------------
 * Why: workers/api/src/thumbs.ts. In short, Supabase Storage refused a page of
 * sixty thumbnails at once (429, 500), and a freshly signed Supabase link is a
 * new address every time, so the browser could never reuse a download.
 *
 * The Worker's links stay the same all day, so a picture seen once comes from
 * the browser's own cache after that. Only thumbnails: the full-size files
 * are still signed by Supabase, because that is what Miro and Copy to project
 * are handed.
 *
 * Never throws. An empty answer -- the Worker unreachable, or R2 not set up
 * yet -- leaves every thumbnail to be signed by Supabase exactly as before.
 */
const ENDPOINT = `${API_BASE}/api/library/thumbs`
const RETRY_MS = 700

async function ask(paths: string[]): Promise<Map<string, string>> {
  const response = await fetch(ENDPOINT, {
    // Never waited on for ever: without links the pictures are signed elsewhere.
    signal: AbortSignal.timeout(15_000),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ paths }),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const body = (await response.json()) as Record<string, unknown>
  const links = new Map<string, string>()
  for (const [path, link] of Object.entries(body)) {
    if (typeof link === 'string' && link) links.set(path, link)
  }
  return links
}

/** Links for the thumbnails of these pictures, by stored path. */
export async function thumbLinksFor(assets: Asset[]): Promise<Map<string, string>> {
  const paths = [...new Set(assets.map((asset) => asset.thumbnailPath).filter(needsSigning))]
  if (paths.length === 0 || !API_BASE) return new Map()
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await ask(paths)
    } catch (error) {
      if (attempt === 1) {
        console.warn('[WonderWorks] thumbnail links from the Worker failed; using Supabase', error)
      } else {
        await new Promise((resolve) => setTimeout(resolve, RETRY_MS))
      }
    }
  }
  return new Map()
}
